// plan-05 S04: connection lines — verbatim port from v1, re-skinned in CRT palette

import type { HexGrid, Hex } from './geometry'
import { getNeighbors, HEX_SIZE_CONSTANT } from './geometry'
import type { MaturityResult } from './maturity'
import { isPrime, neighborMaturitySum, prime3Substrings } from './maturity'
import { SVG_NS, clearGroup } from './render'

// palette tokens for line colors
const GOLD_COLOR = '#FFB000'     // --vt-amber
const RED_COLOR = '#B41414'      // --vt-emergency-red (carveout per plan/bible)
const GREEN_COLOR = '#00B400'    // --vt-king-green

interface LineCandidate {
  ax: number; ay: number; bx: number; by: number
  dist: number; aSeq: number; bSeq: number
  maturity: number; nSum: number
}

function segmentsIntersect(
  x1: number, y1: number, x2: number, y2: number,
  x3: number, y3: number, x4: number, y4: number,
): boolean {
  const dx1 = x2 - x1, dy1 = y2 - y1
  const dx2 = x4 - x3, dy2 = y4 - y3
  const denom = dx1 * dy2 - dy1 * dx2
  if (Math.abs(denom) < 1e-10) return false
  const dx3 = x3 - x1, dy3 = y3 - y1
  const t = (dx3 * dy2 - dy3 * dx2) / denom
  const u = (dx3 * dy1 - dy3 * dx1) / denom
  const eps = 1e-8
  return t > eps && t < 1 - eps && u > eps && u < 1 - eps
}

function zigzagPoints(
  x1: number, y1: number, x2: number, y2: number,
  zags: number, amplitude: number,
): string {
  const pts: string[][] = [[String(x1), String(y1)]]
  const dx = x2 - x1, dy = y2 - y1
  const len = Math.sqrt(dx * dx + dy * dy)
  const ux = dx / len, uy = dy / len
  const px = -uy, py = ux
  const steps = zags * 2
  for (let i = 1; i < steps; i++) {
    const t = i / steps
    const mx = x1 + t * dx
    const my = y1 + t * dy
    const dir = (i % 2 === 1) ? 1 : -1
    pts.push([String(mx + dir * amplitude * px), String(my + dir * amplitude * py)])
  }
  pts.push([String(x2), String(y2)])
  return pts.map(p => p.join(',')).join(' ')
}

function curvePath(x1: number, y1: number, x2: number, y2: number, apexDeg: number): string {
  const dx = x2 - x1, dy = y2 - y1
  const len = Math.sqrt(dx * dx + dy * dy)
  const ux = dx / len, uy = dy / len
  const px = -uy, py = ux
  const offset = len * Math.tan((apexDeg / 2) * Math.PI / 180)
  const mx = (x1 + x2) / 2 + offset * px
  const my = (y1 + y2) / 2 + offset * py
  return `M ${x1},${y1} Q ${mx},${my} ${x2},${y2}`
}

export function drawConnectionLines(
  grid: HexGrid, result: MaturityResult, linesGroup: SVGGElement,
): void {
  clearGroup(linesGroup)
  const { hits, eligible, bordersBlank } = result
  const { ordered, centerHex } = grid

  // group eligible hexes by hit count
  const byMaturity = new Map<number, Hex[]>()
  for (const h of ordered) {
    if (!eligible.has(h.seqNum)) continue
    const v = hits[h.seqNum]
    if (!byMaturity.has(v)) byMaturity.set(v, [])
    byMaturity.get(v)!.push(h)
  }

  const candidates: LineCandidate[] = []
  const seen = new Set<string>()

  byMaturity.forEach((group) => {
    if (group.length < 2) return

    for (const a of group) {
      let furthest: Hex | null = null
      let maxDist = -1
      for (const b of group) {
        if (b === a) continue
        const dx = a.cx - b.cx, dy = a.cy - b.cy
        const d = dx * dx + dy * dy
        if (d > maxDist) { maxDist = d; furthest = b }
      }
      if (!furthest) continue

      const key = [a.seqNum, furthest.seqNum].sort((x, y) => x - y).join('-')
      if (seen.has(key)) continue
      seen.add(key)

      const sumA = neighborMaturitySum(grid, a, hits, eligible)
      const sumB = neighborMaturitySum(grid, furthest, hits, eligible)
      const nSum = Math.max(sumA, sumB)

      candidates.push({
        ax: a.cx, ay: a.cy, bx: furthest.cx, by: furthest.cy,
        dist: maxDist, aSeq: a.seqNum, bSeq: furthest.seqNum,
        maturity: hits[a.seqNum], nSum,
      })
    }
  })

  // sort by maturity descending, distance tiebreaker
  candidates.sort((a, b) => b.maturity - a.maturity || b.dist - a.dist)

  const accepted: LineCandidate[] = []
  const usedEndpoints = new Set<number>()

  for (const c of candidates) {
    const aIsCenter = c.aSeq === centerHex.seqNum
    const bIsCenter = c.bSeq === centerHex.seqNum
    if (!aIsCenter && usedEndpoints.has(c.aSeq)) continue
    if (!bIsCenter && usedEndpoints.has(c.bSeq)) continue

    const crosses = accepted.some(e =>
      segmentsIntersect(c.ax, c.ay, c.bx, c.by, e.ax, e.ay, e.bx, e.by),
    )
    if (crosses) continue

    accepted.push(c)
    if (!aIsCenter) usedEndpoints.add(c.aSeq)
    if (!bIsCenter) usedEndpoints.add(c.bSeq)
  }

  // draw accepted lines
  for (const c of accepted) {
    const { ax, ay, bx, by, nSum } = c
    const prime = isPrime(nSum)
    const even = nSum % 2 === 0
    const isGreen = bordersBlank.has(c.aSeq) || bordersBlank.has(c.bSeq)
    const lineColor = isGreen ? GREEN_COLOR : (prime ? GOLD_COLOR : RED_COLOR)

    let el: SVGElement

    if (prime) {
      el = document.createElementNS(SVG_NS, 'line')
      el.setAttribute('x1', String(ax)); el.setAttribute('y1', String(ay))
      el.setAttribute('x2', String(bx)); el.setAttribute('y2', String(by))
      el.setAttribute('stroke', lineColor)
      el.setAttribute('stroke-width', '3')
      el.setAttribute('stroke-linecap', 'round')
    } else if (even) {
      const zags = Math.max(1, Math.floor(nSum / 2))
      const dx = bx - ax, dy = by - ay
      const segLen = Math.sqrt(dx * dx + dy * dy) / (zags * 2)
      const amplitude = segLen
      el = document.createElementNS(SVG_NS, 'polyline')
      el.setAttribute('points', zigzagPoints(ax, ay, bx, by, zags, amplitude))
      el.setAttribute('fill', 'none')
      el.setAttribute('stroke', lineColor)
      el.setAttribute('stroke-width', '3')
      el.setAttribute('stroke-linejoin', 'miter')
      el.setAttribute('stroke-linecap', 'round')
    } else {
      const apexDeg = 3 * nSum
      el = document.createElementNS(SVG_NS, 'path')
      el.setAttribute('d', curvePath(ax, ay, bx, by, apexDeg))
      el.setAttribute('fill', 'none')
      el.setAttribute('stroke', lineColor)
      el.setAttribute('stroke-width', '3')
      el.setAttribute('stroke-linecap', 'round')
    }

    linesGroup.appendChild(el)
  }

  // top-2 striped line
  if (accepted.length > 0) {
    const top = accepted[0]
    const { ax, ay, bx, by } = top
    const dx = bx - ax, dy = by - ay
    const len = Math.sqrt(dx * dx + dy * dy)
    const dashSize = Math.max(4, len / 12)

    const stripeGold = document.createElementNS(SVG_NS, 'line')
    stripeGold.setAttribute('x1', String(ax)); stripeGold.setAttribute('y1', String(ay))
    stripeGold.setAttribute('x2', String(bx)); stripeGold.setAttribute('y2', String(by))
    stripeGold.setAttribute('stroke', GOLD_COLOR)
    stripeGold.setAttribute('stroke-width', '6')
    stripeGold.setAttribute('stroke-linecap', 'butt')
    stripeGold.setAttribute('stroke-dasharray', `${dashSize} ${dashSize}`)
    stripeGold.setAttribute('stroke-dashoffset', '0')
    linesGroup.appendChild(stripeGold)

    const stripeRed = document.createElementNS(SVG_NS, 'line')
    stripeRed.setAttribute('x1', String(ax)); stripeRed.setAttribute('y1', String(ay))
    stripeRed.setAttribute('x2', String(bx)); stripeRed.setAttribute('y2', String(by))
    stripeRed.setAttribute('stroke', RED_COLOR)
    stripeRed.setAttribute('stroke-width', '6')
    stripeRed.setAttribute('stroke-linecap', 'butt')
    stripeRed.setAttribute('stroke-dasharray', `${dashSize} ${dashSize}`)
    stripeRed.setAttribute('stroke-dashoffset', String(dashSize))
    linesGroup.appendChild(stripeRed)
  }
}

export function drawMaturityChain(
  grid: HexGrid, hits: number[], chainGroup: SVGGElement,
): void {
  clearGroup(chainGroup)

  const byLevel = new Map<number, Hex>()
  for (const h of grid.ordered) {
    const v = hits[h.seqNum]
    if (v === 0) continue
    if (!byLevel.has(v) || h.seqNum > byLevel.get(v)!.seqNum) byLevel.set(v, h)
  }

  const levels = [...byLevel.keys()].sort((a, b) => a - b)
  if (levels.length < 2) return

  for (let i = 0; i < levels.length - 1; i++) {
    const lo = levels[i], hi = levels[i + 1]
    if (hi !== lo + 1) continue

    const a = byLevel.get(lo)!
    const b = byLevel.get(hi)!

    const shadow = document.createElementNS(SVG_NS, 'line')
    shadow.setAttribute('x1', String(a.cx)); shadow.setAttribute('y1', String(a.cy))
    shadow.setAttribute('x2', String(b.cx)); shadow.setAttribute('y2', String(b.cy))
    shadow.setAttribute('stroke', 'rgba(0,0,0,0.35)')
    shadow.setAttribute('stroke-width', '3.5')
    shadow.setAttribute('stroke-linecap', 'round')
    shadow.setAttribute('stroke-dasharray', '4 5')
    chainGroup.appendChild(shadow)

    const line = document.createElementNS(SVG_NS, 'line')
    line.setAttribute('x1', String(a.cx)); line.setAttribute('y1', String(a.cy))
    line.setAttribute('x2', String(b.cx)); line.setAttribute('y2', String(b.cy))
    line.setAttribute('stroke', 'rgba(255,255,255,0.82)')
    line.setAttribute('stroke-width', '1.8')
    line.setAttribute('stroke-linecap', 'round')
    line.setAttribute('stroke-dasharray', '4 5')
    chainGroup.appendChild(line)
  }
}

// cycle detection for prime-position closed-cycle fills
function findCycles(adjMap: Map<number, Set<number>>, nodeList: number[]): number[][] {
  const cycles: number[][] = []
  const visited = new Set<string>()

  function dfs(start: number, current: number, path: number[], pathSet: Set<number>): void {
    const neighbors = adjMap.get(current) ?? new Set()
    for (const nb of neighbors) {
      if (nb === start && path.length >= 3) {
        const cycle = path.slice()
        const minVal = Math.min(...cycle)
        const minIdx = cycle.indexOf(minVal)
        const normalized = [...cycle.slice(minIdx), ...cycle.slice(0, minIdx)]
        const key = normalized.join(',')
        const revKey = [...normalized].reverse().join(',')
        if (!cycles.some(c => c.join(',') === key || c.join(',') === revKey)) {
          cycles.push(normalized)
        }
        continue
      }
      if (pathSet.has(nb)) continue
      const edgeKey = `${current}-${nb}`
      if (visited.has(edgeKey)) continue
      visited.add(edgeKey)
      path.push(nb)
      pathSet.add(nb)
      dfs(start, nb, path, pathSet)
      path.pop()
      pathSet.delete(nb)
    }
  }

  for (const node of nodeList) {
    const pathSet = new Set([node])
    dfs(node, node, [node], pathSet)
  }
  return cycles
}

export function drawPrimeLines(
  grid: HexGrid, hits: number[], primeLinesGroup: SVGGElement,
): void {
  clearGroup(primeLinesGroup)

  const qualifying = grid.ordered.filter(h => isPrime(h.seqNum) && hits[h.seqNum] === 1)
  if (qualifying.length < 2) return

  // find nearest pairs
  const pairs: Array<{ a: Hex; b: Hex; dist: number }> = []
  for (let i = 0; i < qualifying.length; i++) {
    for (let j = i + 1; j < qualifying.length; j++) {
      const a = qualifying[i], b = qualifying[j]
      const dx = a.cx - b.cx, dy = a.cy - b.cy
      pairs.push({ a, b, dist: dx * dx + dy * dy })
    }
  }
  pairs.sort((x, y) => x.dist - y.dist)

  const connectionCount = new Map<number, number>()
  const adjMap = new Map<number, Set<number>>()
  for (const h of qualifying) {
    connectionCount.set(h.seqNum, 0)
    adjMap.set(h.seqNum, new Set())
  }

  for (const { a, b } of pairs) {
    if (connectionCount.get(a.seqNum)! >= 2) continue
    if (connectionCount.get(b.seqNum)! >= 2) continue

    connectionCount.set(a.seqNum, connectionCount.get(a.seqNum)! + 1)
    connectionCount.set(b.seqNum, connectionCount.get(b.seqNum)! + 1)
    adjMap.get(a.seqNum)!.add(b.seqNum)
    adjMap.get(b.seqNum)!.add(a.seqNum)

    const line = document.createElementNS(SVG_NS, 'line')
    line.setAttribute('x1', String(a.cx)); line.setAttribute('y1', String(a.cy))
    line.setAttribute('x2', String(b.cx)); line.setAttribute('y2', String(b.cy))
    line.setAttribute('stroke', '#000000')
    line.setAttribute('stroke-width', '2')
    line.setAttribute('stroke-linecap', 'round')
    primeLinesGroup.appendChild(line)
  }

  // closed-cycle fills
  const hexBySeq = new Map<number, Hex>()
  for (const h of qualifying) hexBySeq.set(h.seqNum, h)

  const nodeList = [...adjMap.keys()]
  const cycles = findCycles(adjMap, nodeList)

  for (const cycle of cycles) {
    if (cycle.length < 3 || cycle.length > 12) continue
    const pts = cycle.map(seq => {
      const h = hexBySeq.get(seq)!
      return `${h.cx},${h.cy}`
    }).join(' ')

    const fill = document.createElementNS(SVG_NS, 'polygon')
    fill.setAttribute('points', pts)
    fill.setAttribute('fill', 'rgba(0,0,0,0.32)')
    fill.setAttribute('stroke', 'none')
    primeLinesGroup.insertBefore(fill, primeLinesGroup.firstChild)
  }
}

export function drawGoldenRings(
  grid: HexGrid, result: MaturityResult, ringGroup: SVGGElement,
): void {
  clearGroup(ringGroup)

  if (!prime3Substrings(result.hashStr)) return

  const outermost = grid.hexes.filter(h => getNeighbors(grid, h).length < 6)
  for (const h of outermost) {
    const pts: string[] = []
    const r = HEX_SIZE_CONSTANT + 3.5
    for (let i = 0; i < 6; i++) {
      const a = (Math.PI / 3) * i
      pts.push(`${h.cx + r * Math.cos(a)},${h.cy + r * Math.sin(a)}`)
    }

    const poly = document.createElementNS(SVG_NS, 'polygon')
    poly.setAttribute('points', pts.join(' '))
    poly.setAttribute('fill', 'none')
    poly.setAttribute('stroke', '#FFB000')
    poly.setAttribute('stroke-width', '1.5')
    poly.setAttribute('opacity', '0.85')
    poly.setAttribute('filter', 'url(#vt-ring-glow)')
    ringGroup.appendChild(poly)
  }
}
